/**
 * ConnectorManager — unified lifecycle & mount-target allocation.
 *
 * Manages the full lifecycle of every connector declared in `skaile.yaml`,
 * whether it exposes a `filesystem` face, a `tools` face, or both.
 *
 * @docLink packages/connectors/concepts#connector-manager
 */

import { mkdir } from "node:fs/promises";
import { dirname, resolve } from "node:path";
import type { CatalogEntry } from "@skaile/workspaces/core";
import { withFsDeadline } from "@skaile/workspaces/core";
import { createLogger } from "@skaile/workspaces/core/logging";
import { pluginRegistry } from "@skaile/workspaces/plugin-registry";
import type { TelemetryProvider, Trace } from "@skaile/workspaces/telemetry";
import type { ConnectorStatusEvent, Logger } from "@skaile/workspaces/types";
import * as z from "zod";
import { tryGetConnector } from "./connector-registry.js";
import type {
  ConnectContext,
  Connector,
  ConnectorChangeEvent,
  ConnectorDeclaration,
  ConnectorFieldSpec,
  ConnectorHandle,
  ConnectorStartupReport,
  FailedConnectorInfo,
  RuntimeSkillDescriptor,
  TokenMediator,
  ToolFace,
} from "./connector-types.js";
import { ConnectorFieldMissingError, ConnectorStartupError } from "./connector-types.js";
import type { FleetHealthVerdict } from "./fleet-utils.js";
import type { PreMintedSecretProvider, SecretProvider, SecretProviderChain } from "./secrets.js";
import type {
  ConnectorContent,
  ConnectorEntry,
  ListOptions,
  OperationDescriptor,
  SearchOptions,
  SearchResult,
  SyncOptions,
  SyncResult,
  WatchHandle,
  WatchOptions,
} from "./shared-types.js";

// ── Internal state ────────────────────────────────────────────────────────────

/**
 * Internal per-connector tracking record.
 */
interface ActiveConnector {
  connector: Connector;
  handle: ConnectorHandle;
  declaration: ConnectorDeclaration;
  watchHandle?: WatchHandle;
}

interface LifecycleResult {
  id: string;
  ok: boolean;
  error?: string;
  detail?: string;
}

export interface SyncConnectorResult {
  id: string;
  ok: boolean;
  error?: string;
}

// ── Module-level helpers ──────────────────────────────────────────────────────

const KNOWN_PREFIXES = ["env:", "forge:", "op:", "kp:", "oauth:"];

function hasKnownPrefix(ref: string): boolean {
  return KNOWN_PREFIXES.some((p) => ref.startsWith(p));
}

/**
 * True when `child` is strictly nested under `parent` (not equal). Both paths
 * must already be absolute and normalized (as `resolveMountTarget` returns).
 */
function isStrictlyNested(child: string, parent: string): boolean {
  return child.startsWith(`${parent}/`);
}

/**
 * Resolve all declared fields for a connector through the secret chain.
 */
async function resolveFields(
  declaration: ConnectorDeclaration,
  fields: ConnectorFieldSpec[],
  chain: SecretProviderChain,
): Promise<Record<string, string>> {
  const resolved: Record<string, string> = {};

  for (const field of fields) {
    let value: string | undefined;

    if (field.type === "oauth") {
      const account = resolved.account ?? "";
      const oauthRef = `oauth:${field.oauthService}:${account}`;
      await chain.prepareRef(oauthRef); // may throw OAuthRequiredError
      value = chain.resolve(oauthRef);
    } else {
      const rawValue = declaration.options?.[field.key];
      if (rawValue !== undefined) {
        const raw = String(rawValue);
        value = hasKnownPrefix(raw) ? chain.resolve(raw) : raw;
      } else {
        value = field.default;
      }
    }

    if (value === undefined && field.required) {
      throw new ConnectorFieldMissingError({
        connectorId: declaration.id,
        field,
        triedRef: String(declaration.options?.[field.key] ?? "(none)"),
        availableProviders: chain.capabilities(),
      });
    }

    if (value !== undefined) resolved[field.key] = value;
  }

  return resolved;
}

/**
 * Build a default `RuntimeSkillDescriptor` for a connector that does not implement `describeSkill()`.
 */
function buildDefaultSkillDescriptor(
  connector: Connector,
  declaration: ConnectorDeclaration,
  handle: ConnectorHandle,
): RuntimeSkillDescriptor {
  const ops = connector.tools?.describeOperations(handle) ?? [];
  return {
    id: declaration.id,
    name: `${connector.name} — ${declaration.id}`,
    description: `${connector.name} connector (${declaration.id})`,
    category: connector.name,
    access: declaration.access,
    toolNames: ops.map((o) => o.name),
  };
}

/**
 * Render a skill body string from a descriptor and operation list.
 */
function buildSkillBody(
  descriptor: RuntimeSkillDescriptor,
  ops: OperationDescriptor[],
  shellAccess: boolean,
): string {
  const lines: string[] = [
    `## ${descriptor.name}`,
    "",
    descriptor.description,
    `Access: ${descriptor.access}`,
    "",
  ];

  if (ops.length > 0) {
    lines.push("### Operations", "");
    for (const op of ops) {
      const argStr = op.args
        .map((a) =>
          a.required
            ? `--${a.name} ${a.type.toUpperCase()}`
            : `[--${a.name} ${a.type.toUpperCase()}]`,
        )
        .join(" ");
      if (shellAccess) {
        lines.push(`**${op.name}** — ${op.description}`);
        lines.push(
          `  skaile connector ${descriptor.id} ${op.name}${argStr ? ` ${argStr}` : ""}`,
          "",
        );
      } else {
        lines.push(`**${op.name}** — ${op.description}`);
        lines.push(`  connector_exec connector_id="${descriptor.id}" operation="${op.name}"`, "");
      }
    }
  }

  lines.push("### Base operations", "");
  if (shellAccess) {
    lines.push(
      `  skaile connector ${descriptor.id} read <path>`,
      `  skaile connector ${descriptor.id} list [path]`,
      `  skaile connector ${descriptor.id} search <query>`,
    );
  } else {
    lines.push(
      `  connector_read connector_id="${descriptor.id}" path=<path>`,
      `  connector_list_entries connector_id="${descriptor.id}"`,
      `  connector_search connector_id="${descriptor.id}" query=<query>`,
    );
  }

  return lines.join("\n");
}

// ── Manager options ───────────────────────────────────────────────────────────

/**
 * Options for the unified `ConnectorManager`. Pass as the second argument to the constructor.
 *
 * Note: constructor signature is `new ConnectorManager(workspaceDir, opts?)` —
 * `workspaceDir` comes first because mountable connectors need it.
 * `secrets` moves into opts (was the first arg in the old `ConnectorManager`).
 */
export interface ConnectorManagerOptions {
  /**
   * Secret provider (chain or bare) for resolving credential references in
   * `describeFields()` and for passing to `Connector.connect()`.
   */
  secrets?: SecretProviderChain | SecretProvider;

  /**
   * Catalog entries for dynamic connector resolution (registry-first, catalog-fallback).
   */
  catalogEntries?: CatalogEntry[];

  /**
   * Backend token mediator — present for `auth: backend` filesystem-face connectors.
   * Wrapped with pre-minted secrets when `preMintedSecrets` is also set.
   */
  tokenMediator?: TokenMediator;

  /**
   * Pre-minted credential store from `session_init`. When set, `auth: backend`
   * connectors use this for the initial mint without a platform round-trip.
   */
  preMintedSecrets?: PreMintedSecretProvider;

  /**
   * Called whenever a connector's connection state changes.
   *
   * Fired from both the bulk `connectAll()` path and the hot-plug `connect()` path.
   * Status values: `"connecting"` → `"connected"` | `"error"`, and `"disconnected"`
   * after `disconnect()` / `disconnectAll()`.
   */
  onStatusChange?: (event: ConnectorStatusEvent) => void;

  /** Optional telemetry provider for span instrumentation. */
  telemetry?: TelemetryProvider;

  /** Optional active trace to attach connector spans to. */
  trace?: Trace;

  /** Deprecated no-op option kept so older callers can pass it during migration. */
  placeholderBarrier?: unknown;

  /** Deprecated no-op option kept so older callers can pass it during migration. */
  fleetHealthPollMs?: number;

  /** Deprecated no-op option kept so older callers can pass it during migration. */
  fleetMountRecoveryProbe?: (target: string) => Promise<boolean>;
}

// ── Manager ───────────────────────────────────────────────────────────────────

/**
 * `ConnectorManager` — manages the full lifecycle of all connectors declared
 * in `skaile.yaml`, whether they expose a filesystem face, a tool face, or both.
 *
 * For filesystem-face connectors, the manager allocates a mount-target
 * directory and passes it as `ctx.mountTarget`; the connector mounts its
 * content there during `connect()`.
 *
 * @docLink packages/connectors/concepts#connector-manager
 */
export class ConnectorManager {
  private active = new Map<string, ActiveConnector>();
  /**
   * Connectors whose `connect()` threw, keyed by id. A failed connector never
   * enters `active`, so without this map its failure would exist only in the
   * log store — invisible to the agent that is about to write into a mount
   * that will never sync. See {@link listFailedConnectors}.
   */
  private readonly failedConnectors = new Map<string, FailedConnectorInfo>();
  // Allocated absolute mount target → owning connector id; collisions rejected at setup, released on disconnect.
  private readonly mountTargets = new Map<string, string>();
  readonly workspaceDir: string;
  private readonly chain?: SecretProviderChain;
  private readonly secrets?: SecretProvider;
  private readonly catalogEntries: CatalogEntry[];
  private readonly tokenMediator?: TokenMediator;
  private readonly preMintedSecrets?: PreMintedSecretProvider;
  private readonly mgrLog: Logger;

  /** Aborted on dispose; threaded into every `connect()` via `ctx.abortSignal`. */
  private readonly disposeController = new AbortController();

  /**
   * @param workspaceDir - Absolute (or relative) working directory. Filesystem-face
   *   connectors mount into `<workspaceDir>/.mounts/<id>/` by default.
   * @param opts - Optional configuration: secrets, catalogEntries, tokenMediator,
   *   preMintedSecrets, onStatusChange, telemetry, trace.
   */
  constructor(
    workspaceDir: string,
    private readonly opts: ConnectorManagerOptions = {},
  ) {
    this.workspaceDir = resolve(workspaceDir);
    this.catalogEntries = opts.catalogEntries ?? [];
    this.tokenMediator = opts.tokenMediator;
    this.preMintedSecrets = opts.preMintedSecrets;

    // Accept SecretProviderChain (has `prepareRef`) or bare SecretProvider
    const secrets = opts.secrets;
    if (secrets && typeof secrets === "object" && "prepareRef" in secrets) {
      this.chain = secrets as SecretProviderChain;
    } else {
      this.secrets = secrets as SecretProvider | undefined;
    }

    this.mgrLog = createLogger({
      kind: "connector",
      subkind: "manager",
      instance: this.workspaceDir,
    });
  }

  // ── Private helpers ──────────────────────────────────────────────────────────

  /**
   * Wrap the user-supplied `TokenMediator` so `reason: 'initial'` calls for an
   * `auth: backend` connector first consult the pre-minted credentials. Falls
   * back to the original mediator for refresh / retry-401 paths. On that
   * fall-through it attaches the connector's `driver` + `providerLinkId` taken
   * from the `declaration` (authoritative routing — a connector cannot override
   * them per call) so the runner mediator can pick the platform refresh `kind`
   * and `id`.
   *
   * Namespace fallback: when no `connector:<id>` pre-mint entry exists, the
   * lookup retries under the legacy `mount:<id>` key. This is a defence-in-depth
   * companion to the platform-side pre-mint namespace alignment — see the
   * 2026-05-19 incident notes and `_devlog/specs/2026-05-07-unified-credential-mediation.md`.
   * When the fallback fires, a `warn` log signals that the platform writer is
   * filing credentials under the pre-unified namespace key.
   */
  private buildWrappedMediator(declaration: ConnectorDeclaration): TokenMediator | undefined {
    const original = this.tokenMediator;
    const preMinted = this.preMintedSecrets;
    const driver = declaration.driver;
    // The runner mediator refreshes against the backend provider link, not the
    // connector id. Prefer the top-level field; fall back to options for wire
    // shapes that nest it there.
    const optionsLink = declaration.options?.providerLinkId;
    const providerLinkId =
      declaration.providerLinkId ?? (typeof optionsLink === "string" ? optionsLink : undefined);
    if (!preMinted) return original;

    return async ({ connectorId, reason }) => {
      if (reason === "initial") {
        let mint = preMinted.mintFor("connector", connectorId);
        if (!mint) {
          const fallback = preMinted.mintFor("mount", connectorId);
          if (fallback) {
            mint = fallback;
            this.mgrLog.warn("connector credential resolved via mount-namespace fallback", {
              connectorId,
            });
          }
        }
        if (mint?.ok) {
          this.mgrLog.debug("initial token served from pre-mint", { connectorId });
          return { ok: true, token: mint.token, expiresAt: mint.expiresAt };
        }
        // Pre-mint exists but failed at the platform mediator — surface the real reason.
        if (mint && !mint.ok) {
          return {
            ok: false,
            code: mint.code ?? "not-configured",
            message:
              mint.message ??
              `Connector '${connectorId}' pre-mint failed at the backend mediator (no detail provided).`,
          };
        }
        // No pre-mint entry: fall through to the original mediator.
      }
      if (!original) {
        return {
          ok: false,
          code: "not-configured",
          message:
            `Connector '${connectorId}' requested ${reason} token but no pre-mint ` +
            `is available and no runner-side mediator is wired.`,
        };
      }
      return original({ connectorId, reason, driver, providerLinkId });
    };
  }

  /**
   * Resolve a connector by driver name: registry first, then catalog.
   */
  private async resolveConnector(driver: string): Promise<Connector> {
    // 1. Check the connector registry.
    const registered = tryGetConnector(driver);
    if (registered) return registered;

    // 2. Catalog lookup — for third-party / project-local connectors that have
    //    not been published to the registry.
    const entry = this.catalogEntries.find((e) => e.kind === "connector" && e.name === driver);
    if (!entry) {
      throw new Error(
        `Unknown connector: "${driver}". ` +
          `Run 'skaile search connector' to see available connectors.`,
      );
    }

    // 3. Dynamic import from source path (dev-only; does not work in compiled binary)
    const entryPoint = (entry.metadata?.entry as string) ?? "./connector.ts";
    const connectorPath = resolve(dirname(entry.source), entryPoint);
    const mod = await import(connectorPath);
    if (typeof mod.createConnector !== "function") {
      throw new Error(
        `Connector "${driver}" at ${connectorPath} does not export createConnector()`,
      );
    }
    const connector: Connector = mod.createConnector();

    // Intentional singleton cache: register a ConnectorTarget whose factory
    // always returns the same resolved instance for catalog-resolved connectors
    // (ported from old ConnectorManager.resolveAdapter). The earlier
    // `tryGetConnector(driver)` returned undefined, so this id is unregistered
    // and the register cannot collide.
    if (!pluginRegistry.get("connector", driver)) {
      pluginRegistry.register("connector", {
        id: driver,
        displayName: driver,
        apiVersion: 1,
        configSchema: z.unknown(),
        create: () => connector,
      });
    }
    return connector;
  }

  /**
   * Compute the absolute mount-target directory for a filesystem-face connector.
   *
   * - `local` driver + absolute source + no explicit target → bind directly to source
   * - otherwise → `decl.mount.target ?? .mounts/<id>`, resolved relative to workspaceDir
   */
  private resolveMountTarget(declaration: ConnectorDeclaration): string {
    const mountConf = declaration.mount;
    if (
      declaration.driver === "local" &&
      !mountConf?.target &&
      mountConf?.source?.startsWith("/")
    ) {
      return mountConf.source;
    }
    const mountPath = mountConf?.target ?? `.mounts/${declaration.id}`;
    return resolve(this.workspaceDir, mountPath);
  }

  // Record absPath → id: same-id is idempotent, an exact-equal collision throws
  // (naming both ids + the path) so a silent shadow fails at setup, nesting warns.
  private reserveMountTarget(id: string, absPath: string): void {
    const owner = this.mountTargets.get(absPath);
    if (owner === id) return;
    if (owner !== undefined) {
      throw new Error(
        `Mount target collision: connectors "${owner}" and "${id}" both resolve to ` +
          `"${absPath}". Each filesystem connector needs a distinct mount.target.`,
      );
    }
    for (const [otherPath, otherId] of this.mountTargets) {
      if (otherId === id) continue;
      if (isStrictlyNested(absPath, otherPath) || isStrictlyNested(otherPath, absPath)) {
        this.mgrLog.warn("nested mount target overlap between connectors", {
          connectorId: id,
          otherConnectorId: otherId,
          target: absPath,
          otherTarget: otherPath,
        });
      }
    }
    this.mountTargets.set(absPath, id);
  }

  /**
   * Remember a connect failure so the agent can be told about it. Called from
   * both the bulk (`attemptConnect`) and hot-plug (`connect`) failure paths.
   */
  private recordFailure(decl: ConnectorDeclaration, error: string): void {
    // A concurrent attempt may already own this id in `active` (the same reason
    // the mount-target release above is conditional). Recording a failure then
    // would have `listConnectors()` call the mount live and
    // `listFailedConnectors()` call it dead — and the prompt would tell the
    // agent a working mount loses its work.
    if (this.active.has(decl.id)) return;
    let mountPath: string | undefined;
    if (decl.mount != null) {
      try {
        mountPath = this.resolveMountTarget(decl);
      } catch {
        // A declaration whose target cannot even be resolved still deserves a
        // failure record — the path is the least important field on it.
      }
    }
    this.failedConnectors.set(decl.id, {
      id: decl.id,
      driver: decl.driver,
      access: decl.access,
      ...(decl.label !== undefined ? { label: decl.label } : {}),
      ...(decl.mount?.source !== undefined ? { source: decl.mount.source } : {}),
      ...(mountPath !== undefined ? { mountPath } : {}),
      required: decl.access === "read-write" && decl.mount != null,
      error,
    });
  }

  /** Release every mount target owned by `id` (on disconnect). */
  private releaseMountTarget(id: string): void {
    for (const [path, owner] of [...this.mountTargets]) {
      if (owner === id) this.mountTargets.delete(path);
    }
  }

  /**
   * Build a `ConnectContext` for a connector, resolving fields and — for
   * filesystem-face connectors — allocating the mount-target directory.
   */
  private async buildConnectContext(
    declaration: ConnectorDeclaration,
    connector: Connector,
  ): Promise<ConnectContext> {
    const ctx: ConnectContext = {
      secrets: this.chain ?? this.secrets,
      // Both filesystem and tool faces use this for the auth: backend initial-mint path.
      tokenMediator: this.buildWrappedMediator(declaration),
      abortSignal: this.disposeController.signal,
    };

    // Backend-auth connectors get their credential from the tokenMediator, not the
    // field chain — skip resolveFields so a required field (e.g. postgres dsn)
    // doesn't throw before connect() can consult the mediator.
    if (this.chain && connector.describeFields && declaration.auth !== "backend") {
      const fields = connector.describeFields();
      ctx.resolvedFields = await resolveFields(declaration, fields, this.chain);
    }

    // Filesystem face: allocate the mount target. Reserve it first so two
    // connectors resolving to the same path collide loudly here instead of
    // silently shadowing each other once both are mounted.
    if (connector.filesystem != null) {
      const absPath = this.resolveMountTarget(declaration);
      this.reserveMountTarget(declaration.id, absPath);
      // The target can be a live FUSE mount (`/skaile/workspace`), so this must
      // stay async + bounded. `recursive: true` is idempotent — an exists probe
      // would only add a second syscall on the same risky path.
      await withFsDeadline(`mkdir ${absPath}`, () => mkdir(absPath, { recursive: true }));
      ctx.mountTarget = absPath;
    }

    return ctx;
  }

  /**
   * Span-wrapping helper for connector lifecycle calls.
   */
  private async withSpan<T>(
    name: string,
    attrs: Record<string, string | number | boolean>,
    fn: () => Promise<T>,
  ): Promise<T> {
    const telemetry = this.opts.telemetry;
    const trace = this.opts.trace;
    if (!telemetry || !trace) return fn();

    const span = telemetry.startSpan(trace, {
      name,
      kind: "connector_op",
      attributes: attrs,
    });

    try {
      const result = await fn();
      telemetry.endSpan(span, { status: "ok" });
      return result;
    } catch (err) {
      telemetry.endSpan(span, {
        status: "error",
        error: (err as Error).message,
      });
      throw err;
    }
  }

  // ── Bulk lifecycle ────────────────────────────────────────────────────────────

  /**
   * Connect all declared connectors. For each connector:
   * 1. Resolves the `Connector` from the registry (or catalog).
   * 2. Calls `setLogger` when available.
   * 3. Resolves fields via `describeFields()` + secret chain.
   * 4. For filesystem-face connectors: allocates mount-target dir.
   * 5. Calls `connector.connect(decl, ctx)`.
   * 6. Tracks in the active map; emits `onStatusChange`.
   *
   * After the loop, if `options.failOnReadWriteError` is set and any read-write
   * connector with a `mount` config failed → throws `ConnectorStartupError`
   * (fail-fast lifecycle semantics for mountable connectors).
   *
   * @param declarations - Connector declarations from `skaile.yaml`.
   * @param options.failOnReadWriteError - Throw `ConnectorStartupError` if any
   *   read-write connector fails. Read-only failures are always tolerated.
   *   Only the eager set is considered — deferred mounts never throw.
   * @param options.deferMounts - Deprecated compatibility option. Cloud
   *   connectors now require already-live host fleet bind mounts and connect
   *   synchronously.
   */
  async connectAll(
    declarations: ConnectorDeclaration[],
    options?: { failOnReadWriteError?: boolean; deferMounts?: boolean },
  ): Promise<ConnectorStartupReport> {
    this.mgrLog.info("connectAll start", { count: declarations.length });

    // Pre-flight: reserve each mount-bearing declaration's resolved target up
    // front so a duplicate-target misconfig throws here, before any connect
    // attempt swallows it into a per-connector result. Only `mount`-bearing
    // declarations can collide — the no-mount default (`.mounts/<id>`) is unique
    // per id — so the cheap guard avoids resolving every connector just to find
    // its target.
    // Roll back what this pass newly claimed when the loop aborts: nothing
    // connected, and a stale claim would fail the corrected retry. Paths an
    // active connector already owns were never claimed here, so they survive.
    const claimed: string[] = [];
    try {
      for (const decl of declarations) {
        if (decl.mount == null) continue;
        const absPath = this.resolveMountTarget(decl);
        const isNew = !this.mountTargets.has(absPath);
        this.reserveMountTarget(decl.id, absPath);
        if (isNew) claimed.push(absPath);
      }
    } catch (err) {
      for (const path of claimed) this.mountTargets.delete(path);
      throw err;
    }

    const results: ConnectorStartupReport["results"] = [];

    for (const decl of declarations) {
      results.push(await this.attemptConnect(decl));
    }

    const report: ConnectorStartupReport = { results };
    this.mgrLog.info("connectAll complete", {
      connected: results.filter((r) => r.connected).length,
      failed: results.filter((r) => !r.connected).length,
    });

    // Throw if any read-write mountable connector (with a mount block) failed.
    // Tool-only connectors (no mount block) are never fail-fast — they are
    // reported in the results like any other failure (fail-fast behaviour for
    // read-write connectors only).
    if (options?.failOnReadWriteError) {
      const declById = new Map(declarations.map((d) => [d.id, d]));
      const failedRw = results
        .filter(
          (r) => !r.connected && r.access === "read-write" && declById.get(r.id)?.mount != null,
        )
        .map((r) => ({ id: r.id, error: r.error ?? "unknown error" }));
      if (failedRw.length > 0) {
        const summary = failedRw.map((f) => `"${f.id}": ${f.error}`).join("; ");
        this.mgrLog.error("connectAll failed: required read-write connector(s) failed", undefined, {
          summary,
        });
        throw new ConnectorStartupError(
          `Required read-write connector(s) failed to connect: ${summary}`,
          report,
          failedRw,
        );
      }
    }

    return report;
  }

  /**
   * Connect a single declaration, emitting `connecting` → `connected | error`.
   * Never throws — failures are recorded in the returned result so `connectAll`
   * can keep going.
   */
  private async attemptConnect(
    decl: ConnectorDeclaration,
    preResolved?: Connector,
  ): Promise<ConnectorStartupReport["results"][number]> {
    this.opts.onStatusChange?.({
      type: "connector_status",
      connectorId: decl.id,
      status: "connecting",
    });

    try {
      const connector = preResolved ?? (await this.resolveConnector(decl.driver));
      const connectorLog = createLogger({
        kind: "connector",
        subkind: connector.name ?? decl.driver,
        instance: decl.id,
      });
      connector.setLogger?.(connectorLog);

      const ctx = await this.buildConnectContext(decl, connector);

      const handle = await this.withSpan(
        "connector.connect",
        {
          "skaile.connector.id": decl.id,
          "skaile.connector.driver": decl.driver,
          "skaile.connector.operation": "connect",
        },
        () => connector.connect(decl, ctx),
      );

      this.active.set(decl.id, {
        connector,
        handle,
        declaration: decl,
      });
      this.failedConnectors.delete(decl.id);
      this.opts.onStatusChange?.({
        type: "connector_status",
        connectorId: decl.id,
        status: "connected",
      });
      return {
        id: decl.id,
        connected: true,
        mountPath: handle.mountPath,
        access: decl.access,
      };
    } catch (err) {
      // Reserved by buildConnectContext (or connectAll's preflight) but never
      // entered `active`, so disconnect() can't reach it — release or the path
      // stays blocked. A live `active` entry means another attempt owns it.
      if (!this.active.has(decl.id)) this.releaseMountTarget(decl.id);
      const msg = err instanceof Error ? err.message : String(err);
      this.recordFailure(decl, msg);
      this.mgrLog.error("connect failed", err, { id: decl.id, driver: decl.driver });
      this.opts.onStatusChange?.({
        type: "connector_status",
        connectorId: decl.id,
        status: "error",
        error: msg,
      });
      return {
        id: decl.id,
        connected: false,
        error: msg,
        access: decl.access,
      };
    }
  }

  /**
   * Compatibility no-op for the removed in-container fleet health gate. Host
   * fleet-managed cloud mounts are verified at connect time by their drivers.
   */
  async reportFleetHealth(_connectorId: string, _verdict: FleetHealthVerdict): Promise<void> {}

  /**
   * Compatibility no-op for the removed deferred rclone mount path.
   */
  async whenDeferredMountsSettled(): Promise<void> {}

  /** Disconnect all connectors and stop all watchers. Best-effort — ignores disconnect errors. */
  async disconnectAll(): Promise<void> {
    this.mgrLog.info("disconnectAll start", { count: this.active.size });
    this.disposeController.abort();
    await this.unwatchAll();
    for (const [id, entry] of this.active) {
      try {
        await entry.connector.disconnect(entry.handle);
      } catch {
        // Best-effort cleanup
      }
      this.opts.onStatusChange?.({
        type: "connector_status",
        connectorId: id,
        status: "disconnected",
      });
    }
    this.active.clear();
    this.mountTargets.clear();
    this.failedConnectors.clear();
    this.mgrLog.info("disconnectAll complete");
  }

  // ── Hot-plug ──────────────────────────────────────────────────────────────────

  /**
   * Connect a single connector (hot-plug). Resolves fields and allocates
   * mount target if the connector has a filesystem face.
   *
   * @returns The `ConnectorHandle` for subsequent operations.
   */
  async connect(declaration: ConnectorDeclaration): Promise<ConnectorHandle> {
    // Emit "connecting" before attempting
    this.opts.onStatusChange?.({
      type: "connector_status",
      connectorId: declaration.id,
      status: "connecting",
    });

    const connector = await this.resolveConnector(declaration.driver);
    const connectorLog = createLogger({
      kind: "connector",
      subkind: connector.name ?? declaration.driver,
      instance: declaration.id,
    });
    connector.setLogger?.(connectorLog);

    try {
      // Inside the try on purpose: buildConnectContext reserves the mount target
      // and then creates it with a deadline-bounded mkdir. That mkdir can now
      // throw (it used to hang forever on a wedged mount), and a throw from out
      // here would sail past the catch below and leak the reservation.
      const ctx = await this.buildConnectContext(declaration, connector);

      const handle = await this.withSpan(
        "connector.connect",
        {
          "skaile.connector.id": declaration.id,
          "skaile.connector.driver": declaration.driver,
          "skaile.connector.operation": "connect",
        },
        () => connector.connect(declaration, ctx),
      );
      this.active.set(declaration.id, { connector, handle, declaration });
      this.failedConnectors.delete(declaration.id);
      this.opts.onStatusChange?.({
        type: "connector_status",
        connectorId: declaration.id,
        status: "connected",
      });
      return handle;
    } catch (err) {
      // buildConnectContext or connect() failed after the target was reserved but
      // before the connector entered `active`, so disconnect() can't reach it —
      // release here or the path stays blocked for other ids until disconnectAll().
      this.releaseMountTarget(declaration.id);
      const msg = err instanceof Error ? err.message : String(err);
      this.recordFailure(declaration, msg);
      this.opts.onStatusChange?.({
        type: "connector_status",
        connectorId: declaration.id,
        status: "error",
        error: msg,
      });
      throw err;
    }
  }

  /**
   * Disconnect a single connector by ID and stop its watcher.
   *
   * A connector that only ever *failed* to connect is not in `active`, so
   * `get(id)` would throw for it. Removing such a declaration is still a
   * legitimate host action — and it is the only way to retract its
   * `## Mount failures` row — so that case is handled as a plain forget rather
   * than an error.
   *
   * @throws {Error} if the connector is neither connected nor recorded as failed
   */
  async disconnect(id: string): Promise<void> {
    if (!this.active.has(id) && this.failedConnectors.has(id)) {
      this.failedConnectors.delete(id);
      this.releaseMountTarget(id);
      this.opts.onStatusChange?.({
        type: "connector_status",
        connectorId: id,
        status: "disconnected",
      });
      return;
    }
    const entry = this.get(id);
    if (entry.watchHandle) {
      try {
        await entry.watchHandle.close();
      } catch {
        // Best-effort
      }
    }
    await this.withSpan(
      "connector.disconnect",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": "disconnect",
      },
      async () => {
        try {
          await entry.connector.disconnect(entry.handle);
        } finally {
          this.active.delete(id);
          this.failedConnectors.delete(id);
          this.releaseMountTarget(id);
          this.opts.onStatusChange?.({
            type: "connector_status",
            connectorId: id,
            status: "disconnected",
          });
        }
      },
    );
  }

  // ── Watch ─────────────────────────────────────────────────────────────────────

  /**
   * Start watchers for all connected connectors that expose a `watch()` method.
   * For filesystem-face connectors, nested-mount-path exclusion logic is applied.
   */
  watchAll(
    callback: (connectorId: string, event: ConnectorChangeEvent) => void,
    options?: WatchOptions,
  ): void {
    // Collect all mount paths for nested-path exclusion
    const allMountPaths = [...this.active.values()]
      .map((e) => e.handle.mountPath)
      .filter((p): p is string => p != null);

    for (const [id, entry] of this.active) {
      if (!entry.connector.watch) continue;

      // Respect explicit watch flag; default: watch read-write, skip read-only.
      // For tool-only connectors (no mount block) this evaluates to
      // `access !== "read-only"`.
      const shouldWatch =
        entry.declaration.mount?.watch ?? entry.declaration.access !== "read-only";
      if (!shouldWatch) continue;

      // For filesystem connectors: build per-connector ignore list excluding
      // mount paths of other connectors nested inside this one.
      let mergedOptions = options;
      const myPath = entry.handle.mountPath;
      if (myPath) {
        const nestedPaths = allMountPaths
          .filter((p) => p !== myPath && p.startsWith(`${myPath}/`))
          .map((p) => `${p}/**`);
        if (nestedPaths.length > 0) {
          mergedOptions = {
            ...options,
            ignored: [...(options?.ignored ?? []), ...nestedPaths],
          };
        }
      }

      try {
        entry.watchHandle = entry.connector.watch(
          entry.handle,
          (event) => callback(id, event),
          mergedOptions,
        );
      } catch {
        // Best-effort — connector may not support watching in current state
      }
    }
  }

  /** Stop all active watchers. */
  async unwatchAll(): Promise<void> {
    for (const [, entry] of this.active) {
      if (entry.watchHandle) {
        try {
          await entry.watchHandle.close();
        } catch {
          // Best-effort
        }
        entry.watchHandle = undefined;
      }
    }
  }

  // ── Session lifecycle ─────────────────────────────────────────────────────────

  async hibernateAll(): Promise<LifecycleResult[]> {
    return this.runLifecycleHook("onHibernate");
  }

  async closeAll(): Promise<LifecycleResult[]> {
    return this.runLifecycleHook("onSessionClose");
  }

  private async runLifecycleHook(
    hook: "onHibernate" | "onSessionClose",
  ): Promise<LifecycleResult[]> {
    const results: LifecycleResult[] = [];
    for (const [id, entry] of this.active) {
      // A deferred mount that never finished connecting (still "connecting", or
      // "error") has a provisional handle (empty state); there's nothing to
      // hibernate/close, and running the hook would claim a flush that never
      // happened. Mirror the syncAll guard (skip anything not "connected").
      if (entry.handle.status !== "connected") {
        results.push({ id, ok: true });
        continue;
      }
      const fn = entry.connector[hook];
      if (!fn) {
        results.push({ id, ok: true });
        continue;
      }
      try {
        const detail = await fn.call(entry.connector, entry.handle);
        results.push({ id, ok: true, detail: detail ?? undefined });
      } catch (err) {
        results.push({
          id,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    return results;
  }

  // ── Discovery ─────────────────────────────────────────────────────────────────

  /**
   * Return the internal `ActiveConnector` entry.
   *
   * @throws {Error} if the connector is not found
   */
  get(id: string): ActiveConnector {
    const entry = this.active.get(id);
    if (!entry) throw new Error(`Connector not found: "${id}"`);
    return entry;
  }

  has(id: string): boolean {
    return this.active.has(id);
  }

  /**
   * Return summary info for all currently connected connectors.
   */
  listConnectors(): Array<{
    id: string;
    driver: string;
    label?: string;
    access: "read-only" | "read-write";
    mountPath?: string;
    status: ConnectorHandle["status"];
  }> {
    return [...this.active.entries()].map(([id, entry]) => ({
      id,
      driver: entry.connector.name,
      label: entry.declaration.label,
      access: entry.declaration.access,
      mountPath: entry.handle.mountPath,
      // Surfaced so wire projections (splitConnectorsForWire) can distinguish a
      // still-connecting deferred mount from a fully-mounted one.
      status: entry.handle.status,
    }));
  }

  /**
   * Return every connector whose `connect()` threw and that has not since
   * connected.
   *
   * A failed connector is deliberately **absent** from `listConnectors()` and
   * `get()` — it never entered the active map, and those surfaces describe what
   * the agent can actually use. That is exactly why this second list exists:
   * without it a failed mount is invisible to the prompt builder, and the agent
   * keeps treating a dead directory as a live, synced workspace.
   *
   * Entries are cleared by a later successful connect of the same id, by
   * `disconnect(id)`, and wholesale by `disconnectAll()`.
   */
  listFailedConnectors(): FailedConnectorInfo[] {
    // Copies: the stored records outlive the call, and a caller that mutated
    // one would silently rewrite what the next prompt build reports.
    return [...this.failedConnectors.values()].map((f) => ({ ...f }));
  }

  /**
   * Lists git-backed connectors, surfacing the fields prompt-builders and the
   * runner's wake-mid-401 refresh handler need.
   */
  listGitConnectors(): Array<{
    id: string;
    source: string;
    exposeAccessToken: boolean;
    auth?: string;
  }> {
    const out: Array<{ id: string; source: string; exposeAccessToken: boolean; auth?: string }> =
      [];
    for (const [, entry] of this.active) {
      if (entry.connector.name !== "git") continue;
      const mountConf = entry.declaration.mount;
      out.push({
        id: entry.declaration.id,
        source: mountConf?.source ?? "",
        exposeAccessToken: mountConf?.exposeAccessToken === true,
        auth: entry.declaration.auth,
      });
    }
    return out;
  }

  // ── Sync ──────────────────────────────────────────────────────────────────────

  /**
   * Pull the latest remote state for a single filesystem-face connector.
   *
   * @throws {Error} if connector is not found or has no filesystem face.
   */
  async sync(id: string, options?: SyncOptions): Promise<SyncResult> {
    const entry = this.get(id);
    if (!entry.connector.filesystem) {
      throw new Error(`Connector "${id}" has no filesystem face`);
    }
    return entry.connector.filesystem.sync(entry.handle, options);
  }

  /**
   * Pull the latest remote state for all connected filesystem-face connectors.
   * Errors from individual connectors are captured and returned rather than thrown.
   */
  async syncAll(options?: SyncOptions): Promise<SyncConnectorResult[]> {
    const results: SyncConnectorResult[] = [];
    for (const [id, entry] of this.active) {
      if (!entry.connector.filesystem) continue;
      if (entry.handle.status !== "connected") continue;
      try {
        await entry.connector.filesystem.sync(entry.handle, options);
        results.push({ id, ok: true });
      } catch (err) {
        results.push({ id, ok: false, error: err instanceof Error ? err.message : String(err) });
      }
    }
    return results;
  }

  // ── Base CRUD dispatch ────────────────────────────────────────────────────────

  /**
   * Throw a consistent error when the tools face is absent.
   */
  private requireToolFace(id: string, entry: ActiveConnector): ToolFace {
    if (!entry.connector.tools) {
      throw new Error(`Connector "${id}" has no tool face`);
    }
    return entry.connector.tools;
  }

  async read(id: string, path: string): Promise<ConnectorContent | null> {
    const entry = this.get(id);
    const tools = this.requireToolFace(id, entry);
    if (!tools.read) {
      throw new Error(`Connector "${id}" tool face does not implement "read"`);
    }
    return this.withSpan(
      "connector.read",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": "read",
      },
      () => tools.read!(entry.handle, path),
    );
  }

  async write(id: string, path: string, content: ConnectorContent): Promise<void> {
    const entry = this.get(id);
    if (entry.declaration.access === "read-only") {
      throw new Error(`Connector "${id}" is read-only`);
    }
    const tools = this.requireToolFace(id, entry);
    if (!tools.write) {
      throw new Error(`Connector "${id}" tool face does not implement "write"`);
    }
    return this.withSpan(
      "connector.write",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": "write",
      },
      () => tools.write!(entry.handle, path, content),
    );
  }

  async delete(id: string, path: string): Promise<boolean> {
    const entry = this.get(id);
    if (entry.declaration.access === "read-only") {
      throw new Error(`Connector "${id}" is read-only`);
    }
    const tools = this.requireToolFace(id, entry);
    if (!tools.delete) {
      throw new Error(`Connector "${id}" tool face does not implement "delete"`);
    }
    return this.withSpan(
      "connector.delete",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": "delete",
      },
      () => tools.delete!(entry.handle, path),
    );
  }

  async list(id: string, path?: string, options?: ListOptions): Promise<ConnectorEntry[]> {
    const entry = this.get(id);
    const tools = this.requireToolFace(id, entry);
    if (!tools.list) {
      throw new Error(`Connector "${id}" tool face does not implement "list"`);
    }
    return this.withSpan(
      "connector.list",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": "list",
      },
      () => tools.list!(entry.handle, path, options),
    );
  }

  async search(id: string, query: string, options?: SearchOptions): Promise<SearchResult[]> {
    const entry = this.get(id);
    const tools = this.requireToolFace(id, entry);
    if (!tools.search) {
      throw new Error(`Connector "${id}" tool face does not implement "search"`);
    }
    return this.withSpan(
      "connector.search",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": "search",
      },
      () => tools.search!(entry.handle, query, options),
    );
  }

  // ── Custom operations ─────────────────────────────────────────────────────────

  /**
   * Execute a custom operation on a connector's tool face.
   *
   * @throws {Error} if connector has no tool face, operation is unknown, or
   *   a write operation is called on a read-only connector.
   */
  async executeOp(id: string, operation: string, args: Record<string, unknown>): Promise<string> {
    const entry = this.get(id);
    const tools = this.requireToolFace(id, entry);

    const ops = tools.describeOperations(entry.handle);
    const opDesc = ops.find((o) => o.name === operation);
    if (!opDesc) {
      throw new Error(
        `Connector "${id}" has no operation "${operation}". Available: ${ops.map((o) => o.name).join(", ")}`,
      );
    }
    if (opDesc.accessLevel === "write" && entry.declaration.access === "read-only") {
      throw new Error(
        `Operation "${operation}" requires write access but connector "${id}" is read-only`,
      );
    }

    return this.withSpan(
      "connector.executeOp",
      {
        "skaile.connector.id": id,
        "skaile.connector.driver": entry.connector.name,
        "skaile.connector.operation": operation,
      },
      () => tools.executeOp(entry.handle, operation, args),
    );
  }

  /**
   * Return the list of operations that a connector's tool face exposes.
   * Returns an empty array when the connector has no tool face.
   */
  getOperations(id: string): OperationDescriptor[] {
    const entry = this.get(id);
    return entry.connector.tools?.describeOperations(entry.handle) ?? [];
  }

  // ── Skill catalog ─────────────────────────────────────────────────────────────

  /**
   * Return `RuntimeSkillDescriptor` for every connected connector.
   * Uses `tools.describeSkill()` when available; falls back to `buildDefaultSkillDescriptor`.
   */
  listSkills(): RuntimeSkillDescriptor[] {
    return [...this.active.values()].map(({ connector, handle, declaration }) => {
      const describeSkill = connector.tools?.describeSkill;
      return describeSkill
        ? describeSkill.call(connector.tools, handle, declaration)
        : buildDefaultSkillDescriptor(connector, declaration, handle);
    });
  }

  /**
   * Return the rendered skill body for a single connector.
   *
   * @param id - Connector ID.
   * @param shellAccess - When true (default), CLI-style commands are used;
   *   false renders SDK tool-call style.
   * @throws {Error} if the connector is not found.
   */
  loadSkill(id: string, shellAccess = true): string {
    const entry = this.get(id);
    const { connector, handle, declaration } = entry;
    const describeSkill = connector.tools?.describeSkill;
    const descriptor = describeSkill
      ? describeSkill.call(connector.tools, handle, declaration)
      : buildDefaultSkillDescriptor(connector, declaration, handle);
    const ops = connector.tools?.describeOperations(handle) ?? [];
    return buildSkillBody(descriptor, ops, shellAccess);
  }
}
